import { useState } from "react";
import {
  Button,
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  useGetEventCategoriesQuery,
  useCreateEventCategoryMutation,
  useUpdateEventCategoryMutation,
  useDeleteEventCategoryMutation,
} from "../../../entities/admin-events";

export const EventCategoriesManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [categoryName, setCategoryName] = useState("");

  const {
    data: categoriesData,
    isLoading,
    refetch,
  } = useGetEventCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] =
    useCreateEventCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateEventCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteEventCategoryMutation();

  const handleOpenCreateDialog = () => {
    setCategoryName("");
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const handleOpenUpdateDialog = (id: number, name: string) => {
    setSelectedCategoryId(id);
    setCategoryName(name);
    setIsUpdateDialogOpen(true);
  };

  const handleCloseUpdateDialog = () => {
    setIsUpdateDialogOpen(false);
    setSelectedCategoryId(null);
  };

  const handleOpenDeleteDialog = (id: number) => {
    setSelectedCategoryId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setSelectedCategoryId(null);
  };

  const handleCreate = async () => {
    if (!categoryName.trim()) {
      alert("Пожалуйста, введите название категории");
      return;
    }

    try {
      await createCategory({ name: categoryName }).unwrap();
      handleCloseCreateDialog();
      refetch();
    } catch (error) {
      console.error("Failed to create category:", error);
      alert("Не удалось создать категорию");
    }
  };

  const handleUpdate = async () => {
    if (!selectedCategoryId || !categoryName.trim()) {
      alert("Пожалуйста, введите название категории");
      return;
    }

    try {
      await updateCategory({
        id: selectedCategoryId,
        body: { name: categoryName },
      }).unwrap();
      handleCloseUpdateDialog();
      refetch();
    } catch (error) {
      console.error("Failed to update category:", error);
      alert("Не удалось обновить категорию");
    }
  };

  const handleDelete = async () => {
    if (!selectedCategoryId) return;

    try {
      await deleteCategory(selectedCategoryId).unwrap();
      handleCloseDeleteDialog();
      refetch();
    } catch (error) {
      console.error("Failed to delete category:", error);
      alert("Не удалось удалить категорию");
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Управление категориями мероприятий</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Добавить категорию
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Загрузка...
                </TableCell>
              </TableRow>
            ) : categoriesData?.categories &&
              categoriesData.categories.length > 0 ? (
              categoriesData.categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.id}</TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        color="primary"
                        onClick={() =>
                          handleOpenUpdateDialog(category.id, category.name)
                        }
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(category.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create category dialog */}
      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogTitle>Создать категорию</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название категории"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Отмена</Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update category dialog */}
      <Dialog open={isUpdateDialogOpen} onClose={handleCloseUpdateDialog}>
        <DialogTitle>Редактировать категорию</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название категории"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Отмена</Button>
          <Button onClick={handleUpdate} disabled={isUpdating}>
            Обновить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить эту категорию?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
          <Button onClick={handleDelete} color="error" disabled={isDeleting}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
